/* ============================================================================
   IFS AUCTION WIDGET — assets/custom-auction.js
   Drives sections/custom-auction-widget.liquid.

   Flute Desk is the source of truth. This file only reads and posts:
     GET  {base}/api/auction/{productId}
     POST {base}/api/auction/verify/start
     POST {base}/api/auction/verify/confirm
     POST {base}/api/auction/{productId}/bid
     POST {base}/api/auction/{productId}/shipping-estimate

   When the state call says the product has no auction, nothing renders and the
   page is left exactly as it was. The buy path is only ever hidden because a
   state said so, never because a request failed.

   Layout and copy come from the "Auction Widget" canvas (second pass,
   2026-09-12): Main, Phone, Scheduled, Verify, Standing, Closing, Shipping,
   Over and Spec. The widget never computes a minimum or an increment; it
   prints minimumBid and increment from the state.

   The subscriber gate comes from the "Auction Subscriber Gate" canvas
   (2026-09-12): Main, StepTwo and Rejoin. Bidding is for stock update
   subscribers and Flute Desk enforces it. The widget's only job is to show the
   tick to the people who need it: a subscriber never sees anything new, a
   non-subscriber sees it once at step one after the first press of Send the
   code (verify/start answers 409 code "join"), and a bidder who left the list
   after verifying sees it under the bid box (bid answers 403 code "rejoin").
   The three calls take an optional `join: true`, sent only when the tick is
   ticked.
   ========================================================================== */

(function () {
  'use strict';

  var TOKEN_KEY = 'ifsBidderToken';
  var EMAIL_KEY = 'ifsBidderEmail';
  var TZ = 'America/Denver';
  var TAGGED_CLASS = 'ifs-auction-tagged';
  var HIDE_BUY_FOR = ['scheduled', 'live', 'closing', 'awaitingPayment', 'secondChance', 'paid'];
  var HISTORY_CAP = 5;

  var FALLBACK_COUNTRIES = [
    ['US', 'United States'], ['AR', 'Argentina'], ['AU', 'Australia'], ['AT', 'Austria'],
    ['BE', 'Belgium'], ['BR', 'Brazil'], ['CA', 'Canada'], ['CL', 'Chile'], ['CN', 'China'],
    ['HR', 'Croatia'], ['CZ', 'Czechia'], ['DK', 'Denmark'], ['EE', 'Estonia'],
    ['FI', 'Finland'], ['FR', 'France'], ['DE', 'Germany'], ['GR', 'Greece'],
    ['HK', 'Hong Kong SAR'], ['HU', 'Hungary'], ['IS', 'Iceland'], ['IN', 'India'],
    ['IE', 'Ireland'], ['IL', 'Israel'], ['IT', 'Italy'], ['JP', 'Japan'],
    ['LV', 'Latvia'], ['LT', 'Lithuania'], ['LU', 'Luxembourg'], ['MY', 'Malaysia'],
    ['MX', 'Mexico'], ['NL', 'Netherlands'], ['NZ', 'New Zealand'], ['NO', 'Norway'],
    ['PL', 'Poland'], ['PT', 'Portugal'], ['RO', 'Romania'], ['SG', 'Singapore'],
    ['SK', 'Slovakia'], ['SI', 'Slovenia'], ['ZA', 'South Africa'], ['KR', 'South Korea'],
    ['ES', 'Spain'], ['SE', 'Sweden'], ['CH', 'Switzerland'], ['TW', 'Taiwan'],
    ['TH', 'Thailand'], ['TR', 'Turkey'], ['AE', 'United Arab Emirates'],
    ['GB', 'United Kingdom']
  ];

  /* ---------------------------------------------------------------- storage */

  function lsGet(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* private mode */ } }
  function lsDel(k) { try { window.localStorage.removeItem(k); } catch (e) { /* private mode */ } }

  /* ------------------------------------------------------------- formatting */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // $5,700, with cents only when the amount has them.
  function money(n) {
    if (n === null || n === undefined || n === '' || isNaN(Number(n))) return '';
    var v = Number(n);
    var cents = Math.round(Math.abs(v) * 100) % 100 !== 0;
    return '$' + v.toLocaleString('en-US', {
      minimumFractionDigits: cents ? 2 : 0,
      maximumFractionDigits: cents ? 2 : 0
    });
  }

  function plural(n, one, many) { return n === 1 ? one : many; }

  var partCache = null;
  function fmtParts(d) {
    if (!partCache) {
      partCache = new Intl.DateTimeFormat('en-US', {
        timeZone: TZ, weekday: 'short', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true
      });
    }
    var o = {};
    partCache.formatToParts(d).forEach(function (p) { o[p.type] = p.value; });
    return o;
  }

  function meridiem(p) { return (p.dayPeriod || '').toLowerCase().replace(/\./g, ''); }
  function clockOf(p) { return p.hour + ':' + p.minute + ' ' + meridiem(p); }

  function toDate(iso) {
    if (!iso) return null;
    var d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  }

  // Mon Sep 14, 1:00 pm Mountain
  function stampLong(iso) {
    var d = toDate(iso); if (!d) return '';
    var p = fmtParts(d);
    return p.weekday + ' ' + p.month + ' ' + p.day + ', ' + clockOf(p) + ' Mountain';
  }

  // Mon Sep 14, 1:14 pm
  function stampShort(iso) {
    var d = toDate(iso); if (!d) return '';
    var p = fmtParts(d);
    return p.weekday + ' ' + p.month + ' ' + p.day + ', ' + clockOf(p);
  }

  // Mon Sep 14 at 1:00 pm Mountain
  function stampAt(iso) {
    var d = toDate(iso); if (!d) return '';
    var p = fmtParts(d);
    return p.weekday + ' ' + p.month + ' ' + p.day + ' at ' + clockOf(p) + ' Mountain';
  }

  // Sun Sep 13
  function stampDay(iso) {
    var d = toDate(iso); if (!d) return '';
    var p = fmtParts(d);
    return p.weekday + ' ' + p.month + ' ' + p.day;
  }

  // 1:07 pm
  function stampTime(iso) {
    var d = toDate(iso); if (!d) return '';
    return clockOf(fmtParts(d));
  }

  // Sep 20 at 1 pm Mountain
  function stampDrop(iso) {
    var d = toDate(iso); if (!d) return '';
    var p = fmtParts(d);
    var t = p.minute === '00' ? p.hour + ' ' + meridiem(p) : clockOf(p);
    return p.month + ' ' + p.day + ' at ' + t + ' Mountain';
  }

  var dayCache = null;
  function dayKey(d) {
    if (!dayCache) {
      dayCache = new Intl.DateTimeFormat('en-CA', {
        timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit'
      });
    }
    return dayCache.format(d);
  }

  // Sun 1:41 pm, or just 1:41 pm when it happened today in Mountain time.
  function stampHistory(iso, now) {
    var d = toDate(iso); if (!d) return '';
    var p = fmtParts(d);
    var t = clockOf(p);
    return dayKey(d) === dayKey(new Date(now)) ? t : p.weekday + ' ' + t;
  }

  function pad(n) { return n < 10 ? '0' + n : String(n); }

  // 22h 14m 08s, 3m 40s under an hour, Closing at zero.
  function countdown(ms) {
    if (!(ms > 0)) return 'Closing';
    var s = Math.floor(ms / 1000);
    var d = Math.floor(s / 86400); s -= d * 86400;
    var h = Math.floor(s / 3600); s -= h * 3600;
    var m = Math.floor(s / 60); s -= m * 60;
    if (d > 0) return d + 'd ' + pad(h) + 'h ' + pad(m) + 'm';
    if (h > 0) return h + 'h ' + pad(m) + 'm ' + pad(s) + 's';
    return m + 'm ' + pad(s) + 's';
  }

  function ordinalTimes(n) {
    if (n === 1) return 'Extended once';
    if (n === 2) return 'Extended twice';
    return 'Extended ' + n + ' times';
  }

  // Runs 24 hours
  function runLength(opensAt, closesAt) {
    var o = toDate(opensAt), c = toDate(closesAt);
    if (!o || !c) return '';
    var hours = Math.round((c.getTime() - o.getTime()) / 3600000);
    if (hours <= 0) return '';
    if (hours % 24 === 0 && hours > 24) {
      var days = hours / 24;
      return 'Runs ' + days + ' ' + plural(days, 'day', 'days');
    }
    return 'Runs ' + hours + ' ' + plural(hours, 'hour', 'hours');
  }

  /* -------------------------------------------------------------- the panel */

  function Auction(mount) {
    this.mount = mount;
    this.base = (mount.getAttribute('data-base') || '').replace(/\/+$/, '');
    this.productId = mount.getAttribute('data-product-id');
    this.headingText = mount.getAttribute('data-heading') || '';

    this.token = lsGet(TOKEN_KEY);
    this.email = lsGet(EMAIL_KEY) || '';

    this.auction = null;
    this.loaded = false;
    this.stopped = false;
    this.skew = 0;
    this.sig = '';

    this.view = 'main';           // main | verify-email | verify-code
    this.pendingMax = null;       // dollars, when verifying in order to bid
    this.verifyName = '';
    this.verifyEmail = this.email;
    this.code = ['', '', '', '', '', ''];
    this.resendAt = 0;

    // The subscriber gate. `gate` shows the tick at step one, `join` is the
    // tick itself, `joinBad` is the red box after a press without it.
    // `reconfirm` means confirm came back 409 with a good code, so the next
    // press at step one confirms again instead of sending another code.
    // `rejoin` shows the tick under the bid box after a refused bid.
    this.gate = false;
    this.gateEmail = '';          // the address the gate was shown for
    this.join = false;
    this.joinBad = false;
    this.reconfirm = false;
    this.rejoin = false;
    this.list = null;             // "joined" or "member", from confirm

    this.draftMax = '';
    this.note = null;             // { text, kind }
    this.flash = null;            // { warn, bold, detail }: the banner after a bid
    this.busy = false;
    this.showAllHistory = false;

    this.shippingOpen = false;
    this.shipCountry = 'US';
    this.shipPostal = '';
    this.shipResult = null;       // { rates, note, error }
    this.shipBusy = false;

    this.tagged = mount.getAttribute('data-tagged') === '1';
    this.placed = mount.getAttribute('data-placed') === '1';
    this.failures = 0;
    this.pollTimer = null;
    this.tickTimer = null;
  }

  Auction.prototype.now = function () { return Date.now() + this.skew; };

  /* ------------------------------------------------------------------- api */

  Auction.prototype.api = function (path, opts) {
    opts = opts || {};
    var headers = {};
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
    if (opts.auth && this.token) headers['Authorization'] = 'Bearer ' + this.token;

    return fetch(this.base + path, {
      method: opts.method || 'GET',
      headers: headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body)
    }).then(function (res) {
      return res.text().then(function (text) {
        var data = null;
        if (text) { try { data = JSON.parse(text); } catch (e) { data = null; } }
        return { ok: res.ok, status: res.status, data: data };
      });
    });
  };

  /* ----------------------------------------------------------- state fetch */

  Auction.prototype.load = function () {
    var self = this;
    return this.api('/api/auction/' + this.productId, { auth: true }).then(function (r) {
      if (!r.ok || !r.data) throw new Error('state ' + r.status);
      self.receive(r.data.auction);
      self.failures = 0;
    }).catch(function () {
      // Show nothing and try again on the next poll. Never touch the buy path:
      // on a tagged product the head rules keep it hidden, which is the safe
      // way round, because Flute Desk has said this item is under auction.
      self.failures += 1;
      if (self.tagged && !self.loaded && self.failures >= 3) self.renderUnreachable();
    }).then(function () {
      self.schedulePoll();
    });
  };

  // Over without a sale: lapsed or cancelled, or the close settled with no
  // bids or a cancellation. The page becomes the ordinary product page again
  // and nothing says there was an auction. A failed auction must not read as
  // a failed product.
  function isOver(a) {
    if (!a) return true;
    if (a.status === 'lapsed' || a.status === 'cancelled') return true;
    var outcome = a.ended && a.ended.outcome;
    return outcome === 'noBids' || outcome === 'cancelled';
  }

  function isSettling(a) {
    if (!a) return false;
    if (a.status === 'closing') return true;
    if (a.phase !== 'ended') return false;
    return !a.ended || !a.ended.outcome || a.ended.outcome === 'settling';
  }

  Auction.prototype.receive = function (auction) {
    if (!auction || isOver(auction)) {
      // No auction, or one that is over without a sale. Render nothing, leave
      // the page alone. On a tagged product this means the tag is stale or
      // still being removed, so the buy path comes back here.
      this.auction = null;
      this.loaded = true;
      this.stopped = true;
      this.mount.hidden = true;
      this.mount.innerHTML = '';
      this.stopTicking();
      this.showBuyPath();
      return;
    }

    var server = toDate(auction.serverTime);
    if (server) this.skew = server.getTime() - Date.now();

    this.auction = auction;
    this.loaded = true;

    this.applyBuyPath();
    this.place();

    var sig = this.signature();
    if (sig !== this.sig) {
      // The auction moved on, so the banner from the last bid has had its say.
      // The rejoin banner stays: it belongs to the tick, not to the last bid.
      if (!this.rejoin) this.flash = null;
      if (this.view === 'main') this.render();
    }
    this.sig = sig;
  };

  Auction.prototype.signature = function () {
    var a = this.auction;
    if (!a) return '';
    return [
      a.phase, a.status, a.currentBid, a.leaderNumber, a.minimumBid, a.increment,
      a.bidCount, a.bidderCount, a.extensionCount, a.opensAt, a.closesAt,
      (a.history || []).length,
      a.me ? JSON.stringify(a.me) : '',
      a.ended ? JSON.stringify(a.ended) : ''
    ].join('|');
  };

  Auction.prototype.pollInterval = function () {
    var a = this.auction;
    if (this.stopped) return null;
    if (!a) return 15000;
    if (isSettling(a)) return 5000;
    if (a.phase === 'ended') return null;
    if (a.phase === 'scheduled') {
      var untilOpen = (toDate(a.opensAt) ? toDate(a.opensAt).getTime() : 0) - this.now();
      return untilOpen > 0 && untilOpen < 60000 ? 5000 : 30000;
    }
    var close = toDate(a.closesAt);
    var left = (close ? close.getTime() : 0) - this.now();
    return left <= 5 * 60 * 1000 ? 2000 : 5000;
  };

  Auction.prototype.schedulePoll = function () {
    clearTimeout(this.pollTimer);
    if (document.hidden) return;
    var ms = this.pollInterval();
    if (ms == null) return;
    var self = this;
    this.pollTimer = setTimeout(function () { self.load(); }, ms);
  };

  /* ------------------------------------------------- placement and buy path */

  Auction.prototype.buyNodes = function () {
    var nodes = [];
    var info = document.querySelector('.product-info') || document;

    var price = info.querySelector('.product-price');
    if (price) nodes.push(price);

    var form = info.querySelector('product-form');
    if (form) {
      var block = form.closest ? form.closest('.product-info__block') : null;
      nodes.push(block || form);
    }

    var sticky = document.querySelector('sticky-atc-panel');
    if (sticky) nodes.push(sticky);

    return nodes;
  };

  Auction.prototype.applyBuyPath = function () {
    var a = this.auction;
    if (!a) return;
    var hide = HIDE_BUY_FOR.indexOf(a.status) !== -1 && !isOver(a);
    this.buyNodes().forEach(function (n) {
      if (hide) n.classList.add('ifs-auction-hidden');
      else n.classList.remove('ifs-auction-hidden');
    });
    // The same switch the layout throws server side from the `auction` tag.
    // Dropping it is what restores the buy path on a lapsed or cancelled
    // auction, and adding it covers an auction whose tag has not landed yet.
    if (hide) document.documentElement.classList.add(TAGGED_CLASS);
    else document.documentElement.classList.remove(TAGGED_CLASS);
  };

  Auction.prototype.showBuyPath = function () {
    document.documentElement.classList.remove(TAGGED_CLASS);
    this.buyNodes().forEach(function (n) { n.classList.remove('ifs-auction-hidden'); });
  };

  Auction.prototype.renderUnreachable = function () {
    var frame = this.mount.querySelector('.ifs-auction');
    if (!frame || frame.getAttribute('data-unreachable')) return;
    frame.setAttribute('data-unreachable', '1');
    frame.removeAttribute('aria-busy');
    frame.innerHTML = '<span class="ifs-auction__line">We can’t reach the auction just now. ' +
      'This page will keep trying.</span>';
  };

  // Move the panel under the title, where the price and buy buttons were.
  Auction.prototype.place = function () {
    this.mount.hidden = false;
    if (this.placed) return;

    var info = document.querySelector('.product-info');
    if (!info) { this.placed = true; return; }

    var anchor = info.querySelector('.product-price');
    if (anchor && anchor.parentNode) {
      this.mount.classList.add('product-info__block');
      anchor.parentNode.insertBefore(this.mount, anchor);
      this.placed = true;
      return;
    }

    var title = info.querySelector('.product-info__title');
    if (title && title.parentNode) {
      this.mount.classList.add('product-info__block');
      title.parentNode.insertBefore(this.mount, title.nextSibling);
    }
    this.placed = true;
  };

  /* ------------------------------------------------------------- rendering */

  Auction.prototype.render = function () {
    var focus = this.captureFocus();
    this.mount.innerHTML = this.html();
    this.restoreFocus(focus);
    this.tick();
    if (this.mount.querySelector('[data-clock], [data-resend]')) this.startTicking();
    else this.stopTicking();
  };

  Auction.prototype.captureFocus = function () {
    var el = document.activeElement;
    if (!el || !this.mount.contains(el)) return null;
    var key = el.getAttribute('data-focus');
    if (!key) return null;
    var o = { key: key };
    try { o.start = el.selectionStart; o.end = el.selectionEnd; } catch (e) { /* not a text input */ }
    return o;
  };

  Auction.prototype.restoreFocus = function (focus) {
    if (!focus) return;
    var el = this.mount.querySelector('[data-focus="' + focus.key + '"]');
    if (!el) return;
    el.focus();
    if (focus.start != null) {
      try { el.setSelectionRange(focus.start, focus.end); } catch (e) { /* not a text input */ }
    }
  };

  Auction.prototype.html = function () {
    var a = this.auction;
    if (!a || isOver(a)) return '';

    var body;
    if (this.view === 'verify-email') body = this.htmlVerifyEmail();
    else if (this.view === 'verify-code') body = this.htmlVerifyCode();
    else if (a.phase === 'ended' || isSettling(a)) body = this.htmlEnded();
    else if (a.phase === 'scheduled') body = this.htmlScheduled();
    else body = this.htmlLive();

    var heading = this.headingText
      ? '<h2 class="ifs-auction__heading">' + esc(this.headingText) + '</h2>'
      : '';

    return heading + '<div class="ifs-auction">' + body + '</div>';
  };

  /* ---- shared pieces ---- */

  Auction.prototype.htmlChip = function () {
    var me = this.auction.me;
    if (!me || me.bidderNumber == null) return '';
    return '<span class="ifs-auction__you">Bidder ' + esc(me.bidderNumber) + '</span>';
  };

  // A tinted banner: the bold sentence with the Bidder chip on its right, then
  // the detail sentence under it.
  Auction.prototype.htmlBand = function (warn, bold, detail) {
    return '<div class="ifs-auction__band' + (warn ? ' ifs-auction__band--warn' : '') + '">' +
      '<span class="ifs-auction__band-head"><b>' + bold + '</b>' + this.htmlChip() + '</span>' +
      (detail ? '<span>' + detail + '</span>' : '') +
    '</div>';
  };

  // The tick from the gate canvas: a 22px square with a 3px white check when
  // ticked, red with the input's outline after a press without it. A native
  // checkbox does the work; the box is drawn beside it.
  Auction.prototype.htmlTick = function () {
    var bad = this.joinBad;
    return '<label class="ifs-auction__tick">' +
      '<input type="checkbox" class="ifs-auction__tick-input" data-field="join" data-focus="join"' +
        (this.join ? ' checked' : '') +
        (bad ? ' aria-invalid="true" aria-describedby="ifs-auction-tick-err"' : '') + '>' +
      '<span class="ifs-auction__box' + (bad ? ' ifs-auction__box--bad' : '') + '" aria-hidden="true">' +
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3"' +
          ' stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>' +
      '</span>' +
      '<span class="ifs-auction__tick-text">' +
        '<b>Add me to the stock update email notification list.</b>' +
        '<span>One email a week with what just arrived, a day before anyone else sees it. Unsubscribe any time.</span>' +
      '</span>' +
    '</label>';
  };

  Auction.prototype.htmlTickError = function () {
    if (!this.joinBad) return '';
    return '<span class="ifs-auction__err" id="ifs-auction-tick-err" role="alert">' +
      'Tick the box to bid. Auctions are for subscribers.</span>';
  };

  Auction.prototype.htmlFirstTimerBar = function () {
    return '<div class="ifs-auction__band ifs-auction__band--bar">' +
      '<span>First time bidding? Verify your email now so a last-minute bid isn’t held up.</span>' +
      '<button type="button" class="ifs-auction__btn ifs-auction__btn--outline ifs-auction__btn--sm" data-act="verify-open">Verify email</button>' +
      '</div>';
  };

  // The banner at the top for a verified bidder: the result of the bid they
  // just placed, or where they stand.
  Auction.prototype.htmlStanding = function () {
    var a = this.auction;
    var me = a.me;

    if (this.flash) return this.htmlBand(this.flash.warn, this.flash.bold, this.flash.detail);
    if (!me) return '';

    if (me.standing === 'winning') {
      return this.htmlBand(false,
        'You’re the high bidder at ' + esc(money(a.currentBid)) + '.',
        'Your maximum is ' + esc(money(me.maxAmount)) + '. If someone bids, we go up for you as far as that.');
    }

    if (me.standing === 'outbid') {
      return this.htmlBand(true,
        'You’ve been outbid.',
        'The bid is ' + esc(money(a.currentBid)) + ', above your maximum of ' + esc(money(me.maxAmount)) + '.');
    }

    return '';
  };

  Auction.prototype.htmlMeta = function () {
    var a = this.auction;
    var hasBids = a.currentBid != null;
    var lead = hasBids
      ? a.bidCount + ' ' + plural(a.bidCount, 'bid', 'bids') + ' · started at ' + money(a.startPrice)
      : 'No bids yet · starts at ' + money(a.startPrice);

    return '<span class="ifs-auction__meta">' +
      '<span>' + esc(lead) + '</span>' +
      '<span class="ifs-auction__meta-sep"> · </span>' +
      '<span class="ifs-auction__meta-ship">plus shipping · ' +
        '<button type="button" class="ifs-auction__dotted" data-act="shipping-toggle">Estimate shipping</button>' +
      '</span>' +
    '</span>';
  };

  // Current bid on the left, Ends on the right with the date under the clock.
  Auction.prototype.htmlFigures = function (opts) {
    var a = this.auction;
    opts = opts || {};
    var hasBids = a.currentBid != null;
    var figure = hasBids ? money(a.currentBid) : money(a.startPrice);

    var ends = '<div class="ifs-auction__stack ifs-auction__ends">' +
      '<span class="ifs-auction__eyebrow">Ends</span>' +
      (opts.closed
        ? '<span class="ifs-auction__clock">Closed</span>'
        : '<span class="ifs-auction__clock" data-clock></span>') +
      '<span class="ifs-auction__muted">' + esc(stampLong(a.closesAt)) + '</span>' +
      (a.extensionCount > 0 ? '<span class="ifs-auction__muted">' + esc(ordinalTimes(a.extensionCount)) + '</span>' : '') +
    '</div>';

    var meta = opts.meta != null
      ? '<span class="ifs-auction__meta">' + opts.meta + '</span>'
      : this.htmlMeta();

    return '<div class="ifs-auction__figures">' +
      '<div class="ifs-auction__stack">' +
        '<span class="ifs-auction__eyebrow">Current bid</span>' +
        '<span class="ifs-auction__figure">' + esc(figure) + '</span>' +
      '</div>' +
      ends +
      meta +
    '</div>';
  };

  Auction.prototype.htmlBox = function () {
    var a = this.auction;
    var me = a.me;
    var raising = !!(me && me.standing === 'winning');
    var standing = !!(this.flash || (me && (me.standing === 'winning' || me.standing === 'outbid')));

    var label, placeholder, action;
    if (raising) {
      label = 'Raise your maximum';
      placeholder = 'More than ' + money(me.maxAmount);
      action = 'Raise';
    } else {
      label = 'Your maximum bid';
      placeholder = 'Enter ' + money(this.minimum()) + ' or more';
      action = this.token ? 'Place bid' : 'Verify and bid';
    }
    // A refused bid from someone who left the list: the button rejoins, and it
    // is dimmed until the tick under the box is ticked.
    if (this.rejoin) action = 'Rejoin and bid';
    var off = this.rejoin && !this.join;

    var out = ['<div class="ifs-auction__group">'];
    out.push('<span class="ifs-auction__label">' + esc(label) + '</span>');
    out.push(
      '<div class="ifs-auction__row">' +
        '<div class="ifs-auction__field">' +
          '<span class="ifs-auction__field-prefix">$</span>' +
          '<input type="text" inputmode="numeric" autocomplete="off" class="ifs-auction__input"' +
            ' data-focus="max" data-field="max" aria-label="' + esc(label) + '"' +
            ' placeholder="' + esc(placeholder) + '" value="' + esc(this.draftMax) + '">' +
        '</div>' +
        '<button type="button" class="ifs-auction__btn' + (off ? ' ifs-auction__btn--off' : '') + '" data-act="bid"' +
          (this.busy ? ' disabled' : '') + '>' + esc(action) + '</button>' +
      '</div>'
    );
    if (this.rejoin) {
      out.push(this.htmlTick());
      out.push(this.htmlTickError());
    }
    if (!standing) {
      out.push('<span class="ifs-auction__muted">We bid for you one step at a time, up to your maximum ' +
        'and never past it. A bid is a commitment to buy.</span>');
    }
    out.push(this.htmlNote());
    out.push('</div>');
    return out.join('');
  };

  Auction.prototype.htmlNote = function () {
    if (!this.note) return '';
    var cls = this.note.kind === 'error' ? 'ifs-auction__err' : 'ifs-auction__small';
    return '<span class="' + cls + '" role="status">' + esc(this.note.text) + '</span>';
  };

  Auction.prototype.htmlHistory = function () {
    var a = this.auction;
    var rows = a.history || [];
    if (!rows.length) return '';

    var now = this.now();
    var mine = a.me && a.me.bidderNumber != null ? a.me.bidderNumber : null;
    var shown = this.showAllHistory ? rows : rows.slice(0, HISTORY_CAP);
    var total = a.bidCount != null ? a.bidCount : rows.length;

    var list = shown.map(function (r) {
      var me = mine != null && r.bidderNumber === mine;
      var who = me ? 'You · Bidder ' + r.bidderNumber : 'Bidder ' + r.bidderNumber;
      return '<div class="ifs-auction__hrow' + (me ? ' ifs-auction__hrow--me' : '') + '">' +
        '<span class="ifs-auction__who">' + esc(who) + '</span>' +
        '<span class="ifs-auction__amt">' + esc(money(r.amount)) + '</span>' +
        '<span class="ifs-auction__at">' + esc(stampHistory(r.at, now)) + '</span>' +
      '</div>';
    }).join('');

    var out = '<div class="ifs-auction__stack ifs-auction__stack--loose">' +
      '<span class="ifs-auction__eyebrow">Bids</span>' +
      '<div class="ifs-auction__hist' + (this.showAllHistory ? ' ifs-auction__hist--scroll' : '') + '">' + list + '</div>';

    if (!this.showAllHistory && rows.length > HISTORY_CAP) {
      out += '<button type="button" class="ifs-auction__showall" data-act="history-all">Show all ' +
        esc(total) + ' ' + plural(total, 'bid', 'bids') + '</button>';
    }
    return out + '</div>';
  };

  // The current step comes from the API. The tiers live in Flute Desk.
  Auction.prototype.htmlSmallPrint = function () {
    var a = this.auction;
    var window_ = a.extensionWindowMinutes || 5;
    var adds = a.extensionMinutes || 5;
    var policy = a.policyUrl || 'https://www.irishflutestore.com/pages/return-policy';
    var step = a.increment != null ? 'Bids rise by ' + money(a.increment) + ' at this price. ' : '';

    return '<span class="ifs-auction__small">' + esc(step) +
      'A bid in the last ' + esc(window_) + ' minutes adds ' + esc(adds) + ' minutes to the clock.' +
      ' Our <a href="' + esc(policy) + '">return and international shipping policy</a>' +
      ' applies, including import duties outside the US.</span>';
  };

  /* ---- scheduled ---- */

  Auction.prototype.htmlScheduled = function () {
    var a = this.auction;
    var out = [];

    out.push(this.htmlFirstTimerBar());

    out.push('<div class="ifs-auction__figures">' +
      '<div class="ifs-auction__stack">' +
        '<span class="ifs-auction__eyebrow">Opens</span>' +
        '<span class="ifs-auction__clock">' + esc(stampDay(a.opensAt)) + '</span>' +
        '<span class="ifs-auction__muted">' + esc(stampTime(a.opensAt)) + ' Mountain</span>' +
      '</div>' +
      '<div class="ifs-auction__stack">' +
        '<span class="ifs-auction__eyebrow">Starts at</span>' +
        '<span class="ifs-auction__figure">' + esc(money(a.startPrice)) + '</span>' +
        '<span class="ifs-auction__muted">no reserve</span>' +
      '</div>' +
      '<span class="ifs-auction__meta">' +
        esc(runLength(a.opensAt, a.closesAt) || 'Runs') + ', closing ' + esc(stampAt(a.closesAt)) + '.' +
      '</span>' +
    '</div>');

    if (this.note) out.push(this.htmlNote());
    out.push(this.htmlSmallPrint());
    return out.join('');
  };

  /* ---- live ---- */

  Auction.prototype.htmlLive = function () {
    var out = [];

    if (!this.token) out.push(this.htmlFirstTimerBar());
    else out.push(this.htmlStanding());

    out.push(this.htmlFigures());
    if (this.shippingOpen) out.push(this.htmlShipping());
    out.push(this.htmlBox());
    out.push(this.htmlHistory());
    out.push(this.htmlSmallPrint());

    return out.join('');
  };

  /* ---- closing and ended ---- */

  Auction.prototype.htmlEnded = function () {
    var a = this.auction;
    var me = a.me;
    var ended = a.ended || {};
    var out = [];

    var counts = a.bidCount + ' ' + plural(a.bidCount, 'bid', 'bids') +
      ' from ' + a.bidderCount + ' ' + plural(a.bidderCount, 'bidder', 'bidders');

    if (isSettling(a)) {
      out.push('<div class="ifs-auction__group">' +
        '<span class="ifs-auction__eyebrow">Auction closed</span>' +
        '<div class="ifs-auction__settling">' +
          '<span class="ifs-auction__spin" aria-hidden="true"></span>' +
          '<span class="ifs-auction__line" role="status">Settling now. This takes a moment.</span>' +
        '</div>' +
      '</div>');
      out.push(this.htmlFigures({ closed: true, meta: esc(counts) }));
      return out.join('');
    }

    var final = ended.finalAmount != null ? ended.finalAmount : a.currentBid;

    if (me && me.won) {
      var until = stampLong(me.invoiceExpiresAt);
      out.push(this.htmlBand(false,
        'You won at ' + esc(money(final)) + '.',
        'Your checkout link is in your email' + (until ? ' and works until ' + esc(until) : '') +
        '. Shipping is added at checkout from your address.'));
      if (me.invoiceUrl) {
        out.push('<a class="ifs-auction__btn ifs-auction__btn--full" href="' + esc(me.invoiceUrl) + '">Open checkout</a>');
      }
    } else if (me && me.offer) {
      var open = stampLong(me.offer.expiresAt);
      out.push(this.htmlBand(false,
        'Second chance: it’s yours at ' + esc(money(me.offer.amount)) + '.',
        'That is your own maximum. The offer is in your email' + (open ? ' and open until ' + esc(open) : '') + '.'));
    }

    out.push('<div class="ifs-auction__stack">' +
      '<span class="ifs-auction__eyebrow">Auction ended · ' + esc(stampShort(a.closesAt)) + '</span>' +
      '<span class="ifs-auction__figure">Sold for ' + esc(money(final)) + '</span>' +
      '<span class="ifs-auction__meta">' + esc(counts) + '</span>' +
    '</div>');

    var thanks = 'Thanks to everyone who bid.';
    if (a.nextDropAt) thanks += ' The next Sunday drop is ' + stampDrop(a.nextDropAt) + '.';
    out.push('<span class="ifs-auction__line">' + esc(thanks) + '</span>');

    out.push(this.htmlHistory());
    return out.join('');
  };

  /* ---- verify ---- */

  Auction.prototype.htmlVerifyEmail = function () {
    var caption = this.pendingMax != null
      ? 'Your maximum bid · ' + money(this.pendingMax)
      : 'Verify your email';

    // The gate, once verify/start has said the address is not on the list:
    // the line, the tick, the error after a press without it, and the button
    // dimmed until the box is ticked. A subscriber never sees any of this.
    var gate = '';
    if (this.gate) {
      gate = '<span class="ifs-auction__line">Bidding is for stock update subscribers, and you aren’t on the list yet.</span>' +
        this.htmlTick() +
        this.htmlTickError();
    }
    var button = this.gate ? 'Join and send the code' : 'Send the code';
    var off = this.gate && !this.join;

    return '<div class="ifs-auction__stack">' +
        '<span class="ifs-auction__eyebrow">' + esc(caption) + '</span>' +
        '<span class="ifs-auction__line">Almost there. Where should we send your code?</span>' +
      '</div>' +
      '<div class="ifs-auction__group">' +
        '<input type="text" class="ifs-auction__control" data-focus="v-name" data-field="v-name"' +
          ' autocomplete="name" placeholder="Name" aria-label="Name" value="' + esc(this.verifyName) + '">' +
        '<input type="email" class="ifs-auction__control" data-focus="v-email" data-field="v-email"' +
          ' autocomplete="email" placeholder="Email" aria-label="Email" value="' + esc(this.verifyEmail) + '">' +
        gate +
        '<button type="button" class="ifs-auction__btn ifs-auction__btn--full' + (off ? ' ifs-auction__btn--off' : '') +
          '" data-act="verify-send"' + (this.busy ? ' disabled' : '') + '>' + button + '</button>' +
        this.htmlNote() +
      '</div>' +
      '<span class="ifs-auction__small">Please bid only what you’re ready to pay. A winner who doesn’t ' +
      'check out within 24 hours loses the item and may not be able to participate in future auctions.</span>';
  };

  Auction.prototype.htmlVerifyCode = function () {
    var confirm = this.pendingMax != null ? 'Confirm and bid ' + money(this.pendingMax) : 'Confirm';
    var complete = this.code.join('').length === 6;

    // No maxlength: a paste or an autofill must be allowed to land all six
    // digits in one box, and the input and paste handlers spread them out.
    var boxes = this.code.map(function (v, i) {
      return '<input type="text" inputmode="numeric" pattern="[0-9]*"' +
        (i === 0 ? ' autocomplete="one-time-code"' : ' autocomplete="off"') +
        ' aria-label="Digit ' + (i + 1) + '"' +
        ' data-focus="code-' + i + '" data-code="' + i + '" value="' + esc(v) + '">';
    }).join('');

    return '<div class="ifs-auction__stack">' +
        '<span class="ifs-auction__eyebrow">Check your email</span>' +
        '<span class="ifs-auction__line">We sent a six-digit code to ' + esc(this.verifyEmail) + '.</span>' +
      '</div>' +
      '<div class="ifs-auction__codes">' + boxes + '</div>' +
      '<div class="ifs-auction__row ifs-auction__row--center">' +
        '<button type="button" class="ifs-auction__btn" data-act="verify-confirm"' +
          (this.busy || !complete ? ' disabled' : '') + '>' + esc(confirm) + '</button>' +
        '<button type="button" class="ifs-auction__showall" data-act="verify-resend" data-resend>Send it again</button>' +
      '</div>' +
      this.htmlNote() +
      // Joiners only. The join lands when the code is confirmed, and the tick
      // at step one is the widget's word that it will. A subscriber sees no line.
      (this.join ? '<span class="ifs-auction__small">You’re on the stock update list now.</span>' : '');
  };

  /* ---- shipping ---- */

  Auction.prototype.htmlShipping = function () {
    var self = this;
    var countries = Auction.countries();

    var options = countries.map(function (c) {
      return '<option value="' + esc(c[0]) + '"' + (c[0] === self.shipCountry ? ' selected' : '') + '>' +
        esc(c[1]) + '</option>';
    }).join('');

    var out = ['<div class="ifs-auction__panel">'];
    out.push('<div class="ifs-auction__stack">' +
      '<span class="ifs-auction__eyebrow">Estimate shipping</span>' +
      '<span class="ifs-auction__line">The rates checkout will offer to your address.</span>' +
    '</div>');

    out.push('<div class="ifs-auction__row">' +
      '<select class="ifs-auction__control" data-focus="ship-country" data-field="ship-country" aria-label="Country">' +
        options +
      '</select>' +
      '<input type="text" class="ifs-auction__control ifs-auction__control--postal" data-focus="ship-postal"' +
        ' data-field="ship-postal" autocomplete="postal-code" placeholder="Postcode" aria-label="Postcode"' +
        ' value="' + esc(this.shipPostal) + '">' +
      '<button type="button" class="ifs-auction__btn ifs-auction__btn--outline" data-act="ship-estimate"' +
        (this.shipBusy ? ' disabled' : '') + '>Estimate</button>' +
    '</div>');

    var r = this.shipResult;
    if (r) {
      if (r.rates && r.rates.length) {
        out.push('<div class="ifs-auction__rates">' + r.rates.map(function (rate) {
          return '<div class="ifs-auction__rate">' +
            '<span>' + esc(rate.title) + '</span>' +
            '<b>' + esc(money(rate.amount)) + '</b>' +
          '</div>';
        }).join('') + '</div>');
        out.push('<span class="ifs-auction__small">' + esc(r.note ||
          'Your final rate is set at checkout. Orders outside the US may owe import duties on delivery.') + '</span>');
      } else {
        out.push('<span class="ifs-auction__err">' + esc(r.error ||
          'We couldn’t get rates for that address. Checkout will show them once you enter your address there.') + '</span>');
      }
    }

    out.push('</div>');
    return out.join('');
  };

  Auction.countries = function () {
    if (Auction._countries) return Auction._countries;
    var list = [];
    var el = document.querySelector('.ifs-auction-countries');
    if (el) {
      try {
        var raw = JSON.parse(el.textContent || '[]');
        list = raw.filter(function (c) { return c && c.code && c.name; })
          .map(function (c) { return [c.code, c.name]; });
      } catch (e) { list = []; }
    }
    if (list.length < 2) list = FALLBACK_COUNTRIES.slice();

    // United States first, then the rest alphabetically.
    var us = list.filter(function (c) { return c[0] === 'US'; });
    var rest = list.filter(function (c) { return c[0] !== 'US'; })
      .sort(function (a, b) { return a[1].localeCompare(b[1]); });

    Auction._countries = us.concat(rest);
    return Auction._countries;
  };

  /* ------------------------------------------------------- per-second tick */

  Auction.prototype.startTicking = function () {
    if (this.tickTimer || document.hidden) return;
    if (!this.mount.querySelector('[data-clock], [data-resend]')) return;
    var self = this;
    this.tickTimer = setInterval(function () { self.tick(); }, 1000);
  };

  Auction.prototype.stopTicking = function () {
    clearInterval(this.tickTimer);
    this.tickTimer = null;
  };

  Auction.prototype.tick = function () {
    var a = this.auction;

    var clock = this.mount.querySelector('[data-clock]');
    if (clock && a && a.closesAt) {
      var close = toDate(a.closesAt);
      clock.textContent = countdown(close ? close.getTime() - this.now() : 0);
    }

    var resend = this.mount.querySelector('[data-resend]');
    if (resend) {
      var left = Math.ceil((this.resendAt - Date.now()) / 1000);
      if (left > 0) {
        resend.textContent = 'Send it again in ' + left + 's';
        resend.setAttribute('disabled', 'disabled');
      } else {
        resend.textContent = 'Send it again';
        resend.removeAttribute('disabled');
      }
    }
  };

  /* --------------------------------------------------------------- actions */

  Auction.prototype.setNote = function (text, kind) {
    this.note = text ? { text: text, kind: kind || 'info' } : null;
  };

  Auction.prototype.readMax = function () {
    var el = this.mount.querySelector('[data-field="max"]');
    return el ? el.value : this.draftMax;
  };

  // From the state, never computed here. The fallbacks only cover a state
  // that is missing the field.
  Auction.prototype.minimum = function () {
    var a = this.auction;
    if (a.minimumBid != null) return a.minimumBid;
    if (a.currentBid != null) return a.currentBid + (a.increment || 0);
    return a.startPrice;
  };

  // Local checks use the same sentences the API returns, so the copy never shifts.
  Auction.prototype.checkMax = function (raw) {
    var a = this.auction;
    var cleaned = String(raw == null ? '' : raw).replace(/[$,\s]/g, '');
    if (!cleaned) return { error: 'Enter ' + money(this.minimum()) + ' or more' };
    if (!/^\d+(\.\d+)?$/.test(cleaned)) return { error: 'Whole dollars only' };
    var value = Number(cleaned);
    if (Math.round(value * 100) % 100 !== 0) return { error: 'Whole dollars only' };

    var me = a.me;
    if (me && me.standing === 'winning' && me.maxAmount != null && value <= me.maxAmount) {
      return { error: 'Enter more than your current maximum of ' + money(me.maxAmount) };
    }
    if (value < this.minimum()) return { error: 'Enter ' + money(this.minimum()) + ' or more' };
    return { value: value };
  };

  Auction.prototype.onBid = function () {
    if (this.busy) return;
    var raw = this.readMax();
    this.draftMax = raw;

    var check = this.checkMax(raw);
    if (check.error) { this.setNote(check.error, 'error'); this.render(); return; }

    if (!this.token) { this.startVerify(check.value); return; }
    if (this.rejoin && !this.join) {
      // Rejoin and bid without the tick: no call, the box goes red.
      this.joinBad = true;
      this.setNote(null);
      this.render();
      return;
    }
    this.postBid(check.value);
  };

  Auction.prototype.postBid = function (value) {
    var self = this;
    var a = this.auction;
    var wasWinning = !!(a.me && a.me.standing === 'winning');

    this.busy = true;
    this.setNote(null);
    this.render();

    var body = { max: value };
    if (this.rejoin && this.join) body.join = true;

    return this.api('/api/auction/' + this.productId + '/bid', {
      method: 'POST', auth: true, body: body
    }).then(function (r) {
      self.busy = false;

      if (r.status === 401 && r.data && r.data.code === 'unverified') {
        lsDel(TOKEN_KEY);
        lsDel(EMAIL_KEY);
        self.token = null;
        self.rejoin = false;
        self.join = false;
        self.joinBad = false;
        self.startVerify(value);
        return;
      }

      if (r.status === 403 && r.data && r.data.code === 'rejoin') {
        // The bidder left the list since verifying. The token is still good,
        // so the bid box stays; the warn banner says why, the tick sits under
        // the box and the button becomes Rejoin and bid.
        self.rejoin = true;
        self.join = false;
        self.joinBad = false;
        self.draftMax = String(value);
        self.flash = {
          warn: true,
          bold: esc(r.data.error || 'You’ve left the stock update list, so bidding is closed.'),
          detail: 'Auctions are for subscribers. Rejoin and your bid goes through.'
        };
        self.setNote(null);
        self.view = 'main';
        self.render();
        return;
      }

      if (!r.ok) {
        var msg = (r.data && r.data.error) || 'That bid did not go through. Please try again.';
        self.draftMax = String(value);
        self.setNote(msg, 'error');
        self.view = 'main';
        self.render();
        return;
      }

      var bid = (r.data && r.data.bid) || {};
      if (r.data && r.data.auction) {
        var server = toDate(r.data.auction.serverTime);
        if (server) self.skew = server.getTime() - Date.now();
        self.auction = r.data.auction;
        self.sig = self.signature();
        self.applyBuyPath();
      }

      // The bid went through, so the rejoin banner and tick have done their job.
      self.rejoin = false;
      self.join = false;
      self.joinBad = false;

      // The banner for this bid. It stays until the auction moves on.
      self.flash = null;
      if (bid.leading && wasWinning) {
        self.flash = {
          warn: false,
          bold: 'Maximum raised to ' + esc(money(bid.max)) + '.',
          detail: 'The bid stays at ' + esc(money(bid.shown)) + '.'
        };
      } else if (bid.leading === false) {
        var detail = 'The bid is ' + esc(money(bid.shown)) + ' and you’re not in the lead.';
        if (bid.extended && bid.closesAt) detail += ' The close moved to ' + esc(stampTime(bid.closesAt)) + '.';
        self.flash = { warn: true, bold: 'Someone’s maximum was higher.', detail: detail };
      }

      self.draftMax = '';
      self.pendingMax = null;
      self.view = 'main';
      self.setNote(null);
      self.render();
      self.schedulePoll();
    }).catch(function () {
      self.busy = false;
      self.draftMax = String(value);
      self.setNote('That bid did not go through. Please try again.', 'error');
      self.render();
    });
  };

  Auction.prototype.startVerify = function (max) {
    this.pendingMax = max == null ? null : max;
    this.view = 'verify-email';
    this.verifyEmail = this.verifyEmail || this.email || '';
    this.code = ['', '', '', '', '', ''];
    this.gate = false;
    this.join = false;
    this.joinBad = false;
    this.reconfirm = false;
    this.rejoin = false;
    this.setNote(null);
    this.render();
  };

  Auction.prototype.onVerifySend = function (isResend) {
    if (this.busy) return;

    var nameEl = this.mount.querySelector('[data-field="v-name"]');
    var emailEl = this.mount.querySelector('[data-field="v-email"]');
    if (nameEl) this.verifyName = nameEl.value;
    if (emailEl) this.verifyEmail = emailEl.value;

    var email = (this.verifyEmail || '').trim();
    if (!email) { this.setNote('Enter your email address.', 'error'); this.render(); return; }

    if (!isResend && this.gate) {
      // The tick is showing. Without it, no call: the box goes red and the
      // error line says why. A changed address starts over without the gate,
      // because the gate was about the address that was sent.
      if (email !== (this.gateEmail || '')) {
        this.gate = false;
        this.join = false;
        this.joinBad = false;
        this.reconfirm = false;
      } else if (!this.join) {
        this.joinBad = true;
        this.setNote(null);
        this.render();
        return;
      } else if (this.reconfirm && this.code.join('').length === 6) {
        // Confirm said the code was right but the address was not on the
        // list. The code is still good: confirm again with the tick.
        this.reconfirm = false;
        this.onVerifyConfirm();
        return;
      }
    }

    var body = { email: email };
    if ((this.verifyName || '').trim()) body.name = this.verifyName.trim();
    if (this.join) body.join = true;

    var self = this;
    this.busy = true;
    this.setNote(null);
    this.render();

    return this.api('/api/auction/verify/start', { method: 'POST', body: body }).then(function (r) {
      self.busy = false;

      if (r.status === 409 && r.data && r.data.code === 'join') {
        // Not on the list and no tick. No code was sent. Show the gate.
        self.gate = true;
        self.gateEmail = email;
        self.join = false;
        self.joinBad = false;
        self.setNote(null);
        self.view = 'verify-email';
        self.render();
        var box = self.mount.querySelector('[data-field="join"]');
        if (box) box.focus();
        return;
      }

      if (!r.ok) {
        var msg = (r.data && r.data.error) || 'We could not send the code. Please try again.';
        self.setNote(msg, 'error');
        if (!isResend) self.view = 'verify-email';
        self.render();
        return;
      }

      var wait = (r.data && r.data.resendIn) || 60;
      self.resendAt = Date.now() + wait * 1000;
      self.view = 'verify-code';
      if (!isResend) self.code = ['', '', '', '', '', ''];
      self.setNote(isResend ? 'We sent another code.' : null);
      self.render();
      if (!isResend) {
        var first = self.mount.querySelector('[data-code="0"]');
        if (first) first.focus();
      }
    }).catch(function () {
      self.busy = false;
      self.setNote('We could not send the code. Please try again.', 'error');
      self.render();
    });
  };

  Auction.prototype.onVerifyConfirm = function () {
    if (this.busy) return;

    var code = this.code.join('');
    if (code.length !== 6) { this.setNote('Enter the six-digit code.', 'error'); this.render(); return; }

    var self = this;
    var email = (this.verifyEmail || '').trim();

    this.busy = true;
    this.setNote(null);
    this.render();

    var body = { email: email, code: code };
    if (this.join) body.join = true;

    return this.api('/api/auction/verify/confirm', {
      method: 'POST', body: body
    }).then(function (r) {
      self.busy = false;

      if (r.status === 409 && r.data && r.data.code === 'join') {
        // The code was right but the address is not on the list and no tick
        // came. The code is still good. Back to step one with the tick
        // showing; the next press there confirms again with join.
        self.gate = true;
        self.gateEmail = email;
        self.join = false;
        self.joinBad = false;
        self.reconfirm = true;
        self.setNote(null);
        self.view = 'verify-email';
        self.render();
        var box = self.mount.querySelector('[data-field="join"]');
        if (box) box.focus();
        return;
      }

      if (!r.ok || !r.data || !r.data.token) {
        var msg = (r.data && r.data.error) || 'That code is not right. Check the email and try again.';
        self.setNote(msg, 'error');
        self.render();
        return;
      }

      self.token = r.data.token;
      self.email = (r.data.bidder && r.data.bidder.email) || email;
      lsSet(TOKEN_KEY, self.token);
      lsSet(EMAIL_KEY, self.email);

      self.list = r.data.list || null;
      self.code = ['', '', '', '', '', ''];
      self.gate = false;
      self.join = false;
      self.joinBad = false;
      self.reconfirm = false;
      self.view = 'main';

      if (self.pendingMax != null) {
        var max = self.pendingMax;
        self.pendingMax = null;
        self.postBid(max);
        return;
      }

      self.setNote('Your email is verified. You can bid now.');
      self.sig = '';
      self.render();
      self.load();
    }).catch(function () {
      self.busy = false;
      self.setNote('That code is not right. Check the email and try again.', 'error');
      self.render();
    });
  };

  Auction.prototype.onEstimate = function () {
    if (this.shipBusy) return;

    var country = this.mount.querySelector('[data-field="ship-country"]');
    var postal = this.mount.querySelector('[data-field="ship-postal"]');
    if (country) this.shipCountry = country.value;
    if (postal) this.shipPostal = postal.value;

    var self = this;
    this.shipBusy = true;
    this.render();

    return this.api('/api/auction/' + this.productId + '/shipping-estimate', {
      method: 'POST',
      body: { country: this.shipCountry, postalCode: (this.shipPostal || '').trim() }
    }).then(function (r) {
      self.shipBusy = false;
      if (!r.ok || !r.data) {
        self.shipResult = { rates: [], error: (r.data && r.data.error) || '' };
      } else {
        self.shipResult = { rates: r.data.rates || [], note: r.data.note, error: r.data.error || '' };
      }
      self.render();
    }).catch(function () {
      self.shipBusy = false;
      self.shipResult = { rates: [], error: '' };
      self.render();
    });
  };

  /* ---- the six code boxes ---- */

  // Write digits into the boxes from index `from`, keep the DOM in step, and
  // put the caret on the last box that got a digit.
  Auction.prototype.fillCode = function (from, digits) {
    var last = from;
    for (var k = 0; k < digits.length && from + k < 6; k++) {
      this.code[from + k] = digits[k];
      last = from + k;
    }
    this.syncCode();
    var target = this.mount.querySelector('[data-code="' + last + '"]');
    if (target) { target.focus(); try { target.select(); } catch (e) { /* fine */ } }
  };

  // The boxes and the Confirm button follow this.code without a re-render, so
  // typing never loses the caret.
  Auction.prototype.syncCode = function () {
    var self = this;
    for (var i = 0; i < 6; i++) {
      var box = this.mount.querySelector('[data-code="' + i + '"]');
      if (box && box.value !== self.code[i]) box.value = self.code[i];
    }
    var confirm = this.mount.querySelector('[data-act="verify-confirm"]');
    if (confirm) {
      var complete = this.code.join('').length === 6;
      if (complete && !this.busy) confirm.removeAttribute('disabled');
      else confirm.setAttribute('disabled', 'disabled');
    }
  };

  /* ---------------------------------------------------------------- events */

  Auction.prototype.bind = function () {
    var self = this;

    this.mount.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-act]') : null;
      if (!btn || !self.mount.contains(btn)) return;
      if (btn.hasAttribute('disabled')) { e.preventDefault(); return; }

      var act = btn.getAttribute('data-act');
      if (act === 'bid') { e.preventDefault(); self.onBid(); }
      else if (act === 'verify-open') { e.preventDefault(); self.startVerify(null); }
      else if (act === 'verify-send') { e.preventDefault(); self.onVerifySend(false); }
      else if (act === 'verify-resend') { e.preventDefault(); self.onVerifySend(true); }
      else if (act === 'verify-confirm') { e.preventDefault(); self.onVerifyConfirm(); }
      else if (act === 'ship-estimate') { e.preventDefault(); self.onEstimate(); }
      else if (act === 'shipping-toggle') {
        e.preventDefault();
        self.shippingOpen = !self.shippingOpen;
        self.render();
        if (self.shippingOpen) {
          var postal = self.mount.querySelector('[data-field="ship-postal"]');
          if (postal) postal.focus();
        }
      } else if (act === 'history-all') {
        e.preventDefault();
        self.showAllHistory = true;
        self.render();
      }
    });

    // A pasted code. The browser would otherwise drop it into one box, so
    // take the digits ourselves and spread them from the focused box onward.
    this.mount.addEventListener('paste', function (e) {
      var el = e.target;
      var codeIndex = el.getAttribute && el.getAttribute('data-code');
      if (codeIndex == null) return;
      var clip = e.clipboardData || window.clipboardData;
      var text = clip ? clip.getData('text') : '';
      var digits = String(text || '').replace(/\D/g, '');
      if (!digits) return;
      e.preventDefault();
      self.fillCode(Number(codeIndex), digits.slice(0, 6));
    });

    this.mount.addEventListener('input', function (e) {
      var el = e.target;

      var codeIndex = el.getAttribute && el.getAttribute('data-code');
      if (codeIndex != null) {
        var i = Number(codeIndex);
        var digits = (el.value || '').replace(/\D/g, '');

        if (digits.length > 1) {
          // A browser filling the whole code into one box, iOS from Mail for
          // example. Spread it out from this box.
          self.fillCode(i, digits.slice(0, 6));
          return;
        }

        // One digit at a time, and only a digit.
        self.code[i] = digits;
        if (el.value !== digits) el.value = digits;
        self.syncCode();
        if (digits && i < 5) {
          var after = self.mount.querySelector('[data-code="' + (i + 1) + '"]');
          if (after) { after.focus(); try { after.select(); } catch (err) { /* fine */ } }
        }
        return;
      }

      var field = el.getAttribute && el.getAttribute('data-field');
      if (field === 'max') self.draftMax = el.value;
      else if (field === 'v-name') self.verifyName = el.value;
      else if (field === 'v-email') self.verifyEmail = el.value;
      else if (field === 'ship-postal') self.shipPostal = el.value;
      else if (field === 'ship-country') self.shipCountry = el.value;
    });

    this.mount.addEventListener('change', function (e) {
      var field = e.target.getAttribute && e.target.getAttribute('data-field');
      if (field === 'ship-country') self.shipCountry = e.target.value;
      else if (field === 'join') {
        self.join = !!e.target.checked;
        self.joinBad = false;
        self.render();
      }
    });

    this.mount.addEventListener('keydown', function (e) {
      var el = e.target;
      var field = el.getAttribute && el.getAttribute('data-field');
      var codeIndex = el.getAttribute && el.getAttribute('data-code');

      if (e.key === 'Enter') {
        if (field === 'max') { e.preventDefault(); self.onBid(); }
        else if (field === 'v-name' || field === 'v-email') { e.preventDefault(); self.onVerifySend(false); }
        else if (field === 'ship-postal') { e.preventDefault(); self.onEstimate(); }
        else if (codeIndex != null) { e.preventDefault(); self.onVerifyConfirm(); }
        return;
      }

      if (e.key === 'Backspace' && codeIndex != null && !el.value) {
        var i = Number(codeIndex);
        if (i > 0) {
          e.preventDefault();
          self.code[i - 1] = '';
          self.syncCode();
          var prev = self.mount.querySelector('[data-code="' + (i - 1) + '"]');
          if (prev) prev.focus();
        }
      }
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        clearTimeout(self.pollTimer);
        self.stopTicking();
      } else if (!self.stopped) {
        self.startTicking();
        self.load();
      }
    });
  };

  /* ------------------------------------------------------------------ init */

  Auction.prototype.destroy = function () {
    this.stopped = true;
    clearTimeout(this.pollTimer);
    this.stopTicking();
    this.showBuyPath();
    if (this.mount.parentNode) this.mount.parentNode.removeChild(this.mount);
  };

  var widgets = [];

  function start(mount) {
    if (!mount || mount.getAttribute('data-ifs-auction-ready')) return;
    if (!mount.getAttribute('data-product-id') || !mount.getAttribute('data-base')) return;
    if (typeof window.fetch !== 'function') return;

    mount.setAttribute('data-ifs-auction-ready', '1');
    var widget = new Auction(mount);
    widgets.push(widget);
    widget.bind();
    widget.load();
  }

  // The panel moves out of its own section wrapper, so when the theme editor
  // removes or re-renders the section we have to take the panel with it.
  function sweep(sectionId) {
    widgets = widgets.filter(function (w) {
      var mine = sectionId != null && w.mount.getAttribute('data-section-id') === String(sectionId);
      if (mine || !document.documentElement.contains(w.mount)) { w.destroy(); return false; }
      return true;
    });
  }

  function init(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll ? scope.querySelectorAll('.ifs-auction-mount') : [];
    Array.prototype.forEach.call(nodes, start);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor re-renders or removes the section.
  document.addEventListener('shopify:section:load', function (e) {
    Auction._countries = null;
    sweep(e.detail && e.detail.sectionId);
    init(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    sweep(e.detail && e.detail.sectionId);
  });
})();
